// ==================================================
// FILE: themes/vanillaStory.js
// ==================================================

/**
 * CHAPTER 1: DEPLOYMENT
 * Days 1-30 (Approximate)
 * Theme: Establishment, first operations, learning enemy tactics
 * Players establish their base, recruit first soldiers, understand the threat
 * Story Goal: Reach "First Strike" mission - prove the unit can execute operations
 */
export const CHAPTER_1 = {
  id: 'chapter_1',
  title: 'Deployment',
  subtitle: 'Boots on the Ground',
  description: 'Your unit has been deployed to an active combat theater. The mission: establish a forward operating base, conduct tactical operations, and achieve strategic objectives against a well-equipped enemy force.',
  
  theme: {
    tone: 'professional_focused',
    color: 'blues_and_grays',
    pacing: 'methodical'
  },
  
  openingNarrative: 'This is what you trained for. Deployment to an active theater. Your unit arrives at a forward operating base with clear orders: establish defensive positions, conduct reconnaissance, and prepare for offensive operations. The enemy is organized, well-equipped, and holding strategic positions. Command expects results. Your soldiers expect leadership. The clock is ticking.',
  
  storyBeats: [
    {
      id: 'ch1_beat_1',
      day: 1,
      title: 'Base Establishment',
      description: 'Your unit arrives and begins establishing the forward operating base. Equipment is unpacked. Perimeters are secured. Operations begin.',
      narrative: 'This is command. A concrete structure in a hostile theater. Limited supplies. Trained personnel. Mission parameters clear.',
      eventHooks: ['supply_delivery', 'terrain_advantage'],
      characterSpeech: {
        protagonist: 'We\'re operational. Let\'s show command what this unit can do.'
      }
    },
    {
      id: 'ch1_beat_2',
      day: 7,
      title: 'First Reconnaissance',
      description: 'Initial reconnaissance operations map enemy positions and movement patterns. Intelligence gathering is critical.',
      narrative: 'Know the battlefield. Know the enemy. Information is the foundation of every successful operation.',
      eventHooks: ['enemy_movement', 'civilian_intel'],
      characterSpeech: {
        intel_officer: 'Enemy is dug in, but they have patterns. We can exploit them.',
        protagonist: 'Then that\'s what we do. We learn. We adapt. We execute.'
      }
    },
    {
      id: 'ch1_beat_3',
      day: 14,
      title: 'Equipment Check',
      description: 'Supply officers confirm equipment readiness. Weapons are clean. Armor is functional. Soldiers are trained.',
      narrative: 'In combat, equipment failure means casualties. We maintain our gear like our lives depend on it. They do.',
      eventHooks: ['equipment_upgrade'],
      characterSpeech: {
        supply_officer: 'Everything checks out. We\'re ready to execute.',
        protagonist: 'Then we execute. First operation goes live.'
      }
    },
    {
      id: 'ch1_beat_4',
      day: 21,
      title: 'First Strike Authorization',
      description: 'Command authorizes first offensive operation. Target identified. Squad assembled. Mission is go.',
      narrative: 'This is the moment. Training becomes reality. We strike fast, we strike hard, and we come home.',
      eventHooks: [],
      missionTrigger: 'first_strike_operation',
      characterSpeech: {
        protagonist: 'Gear up. Mission briefing in five. We show them what we can do.',
        soldier: 'Finally. Let\'s get some.'
      }
    }
  ],
  
  thematicElements: [
    'Professional execution',
    'Learning enemy tactics',
    'Unit cohesion',
    'Establishing operational capability'
  ],
  
  eventPool: ['backdrop', 'threat_low', 'morale_minor'],
  
  failureState: {
    title: 'Operation Compromised',
    description: 'If casualty rate exceeds 40%, command considers redeployment.',
    narrative: 'We took too many losses. Command is reconsidering our deployment.'
  },
  
  successState: {
    title: 'Operational Success',
    description: 'First strike succeeds. Unit proves combat effectiveness. Command authorizes expanded operations.',
    narrative: 'We executed perfectly. Command takes notice. This is just the beginning.'
  }
};

/**
 * CHAPTER 2: ESCALATION
 * Days 31-70 (Approximate)
 * Theme: Expansion, coordination, growing operations
 */
export const CHAPTER_2 = {
  id: 'chapter_2',
  title: 'Escalation',
  subtitle: 'Taking the Initiative',
  description: 'Success breeds opportunity. Your unit has proven itself. Now command authorizes expanded operations, coordination with allied forces, and deeper strikes into enemy territory.',
  
  theme: {
    tone: 'confident_aggressive',
    color: 'oranges_and_reds',
    pacing: 'accelerating'
  },
  
  openingNarrative: 'The first operations were successful. Your unit executed with precision. Now command wants more. Larger operations. Coordinated strikes with allied units. Deeper penetration into enemy territory. The enemy knows about you now. They\'re adapting. But so are you. This is escalation. This is war.',
  
  storyBeats: [
    {
      id: 'ch2_beat_1',
      day: 31,
      title: 'Expanded Authorization',
      description: 'Command authorizes unit expansion. New personnel arrive. Equipment upgraded. Operational scope increases.',
      narrative: 'We\'re growing. More soldiers, more equipment, more responsibility. Command believes in us.',
      eventHooks: ['supply_delivery', 'equipment_upgrade'],
      characterSpeech: {
        commander: 'You\'ve earned this expansion. Don\'t waste it.',
        protagonist: 'We won\'t. Every new soldier makes us stronger.'
      }
    },
    {
      id: 'ch2_beat_2',
      day: 42,
      title: 'Allied Coordination',
      description: 'First coordinated operation with allied forces. Multiple units striking simultaneously.',
      narrative: 'This is bigger than us now. Multiple units. Synchronized strikes. This is combined arms.',
      eventHooks: ['allied_communication', 'allied_request'],
      characterSpeech: {
        allied_officer: 'Your reputation precedes you. Let\'s show them what cooperation looks like.',
        protagonist: 'Agreed. We coordinate, we communicate, we win.'
      }
    },
    {
      id: 'ch2_beat_3',
      day: 54,
      title: 'Enemy Adaptation',
      description: 'Intelligence reports enemy is changing tactics in response to our operations. They\'re learning.',
      narrative: 'The enemy adapts. They study our patterns. This is the chess match of modern warfare.',
      eventHooks: ['enemy_movement', 'enemy_intel'],
      characterSpeech: {
        intel_officer: 'They\'re not static. They\'re evolving their defense.',
        protagonist: 'Then we evolve our offense. Stay unpredictable.'
      }
    },
    {
      id: 'ch2_beat_4',
      day: 66,
      title: 'Major Operation Authorization',
      description: 'Command authorizes major coordinated offensive. This is the biggest operation yet.',
      narrative: 'Multiple units. Air support. Artillery. This is combined arms at scale. This is how wars are won.',
      eventHooks: ['allied_coordination'],
      missionTrigger: 'coordinated_offensive',
      characterSpeech: {
        protagonist: 'This is it. Everything we\'ve built leads here. Execute with precision.'
      }
    }
  ],
  
  thematicElements: [
    'Growth and responsibility',
    'Allied cooperation',
    'Enemy adaptation',
    'Combined arms warfare'
  ],
  
  eventPool: ['backdrop', 'threat_medium', 'morale_mixed', 'faction', 'discovery'],
  
  failureState: {
    title: 'Operational Setback',
    description: 'If major operation fails, command scales back operations.',
    narrative: 'We overreached. Command pulls back our operational authority.'
  },
  
  successState: {
    title: 'Strategic Momentum',
    description: 'Major offensive succeeds. Enemy positions compromised. Strategic initiative shifts.',
    narrative: 'We broke through. The enemy reels. Momentum is ours.'
  }
};

/**
 * CHAPTER 3: CRISIS
 * Days 71-110 (Approximate)
 * Theme: Pressure, losses, testing resolve
 */
export const CHAPTER_3 = {
  id: 'chapter_3',
  title: 'Crisis',
  subtitle: 'The Crucible',
  description: 'Success drew enemy attention. Now they strike back with overwhelming force. Losses mount. Resources dwindle. Leadership is tested.',
  
  theme: {
    tone: 'grim_determined',
    color: 'grays_and_blacks',
    pacing: 'intense'
  },
  
  openingNarrative: 'The enemy counterattacks. Hard. Positions you secured are bombarded. Supply lines are hit. Allied bases go dark. This is the reality of prolonged combat. Success invites escalation. Now your unit faces the hardest test: survival under pressure. Command demands you hold. Your soldiers look to you for answers. There are no easy ones.',
  
  storyBeats: [
    {
      id: 'ch3_beat_1',
      day: 71,
      title: 'Enemy Counteroffensive',
      description: 'Enemy launches coordinated counteroffensive. Multiple positions under assault simultaneously.',
      narrative: 'They\'re hitting us everywhere. This is total war. This is what desperation looks like on both sides.',
      eventHooks: ['enemy_offensive', 'command_breakdown'],
      characterSpeech: {
        tactical: 'We can\'t hold all positions. We need to prioritize.',
        protagonist: 'Then we choose our ground. We hold what matters.'
      }
    },
    {
      id: 'ch3_beat_2',
      day: 83,
      title: 'Supply Crisis',
      description: 'Enemy interdicted supply routes. Ammunition running low. Rations cut. Equipment maintenance suffering.',
      narrative: 'War is logistics. Without supplies, we\'re just targets waiting to run dry.',
      eventHooks: ['supply_interdiction', 'ammunition_shortage'],
      characterSpeech: {
        supply_officer: 'We\'re running on fumes. Another week like this...',
        protagonist: 'Then we make every shot count. No waste. No panic.'
      }
    },
    {
      id: 'ch3_beat_3',
      day: 95,
      title: 'Allied Position Falls',
      description: 'Neighboring allied base overrun. Hundreds dead. Your unit is now isolated.',
      narrative: 'We\'re alone now. No backup. No reinforcement. Just us and the mission.',
      eventHooks: ['allied_collapse', 'morale_crisis'],
      characterSpeech: {
        soldier: 'Sir, people are scared. They\'re asking if we can even win this.',
        protagonist: 'We win by not giving up. We stand. We fight. That\'s how we win.'
      },
      missionTrigger: 'desperate_defense'
    },
    {
      id: 'ch3_beat_4',
      day: 107,
      title: 'The Last Stand',
      description: 'Base is surrounded. Ammunition low. Reinforcements days away. This is survival.',
      narrative: 'This is the moment that defines us. Do we break or do we hold? There\'s no middle ground.',
      eventHooks: [],
      characterSpeech: {
        protagonist: 'Everyone listen. We hold this ground. Not for command. Not for medals. Because we\'re soldiers and this is what we do.'
      }
    }
  ],
  
  thematicElements: [
    'Survival under pressure',
    'Leadership in crisis',
    'Cost of war',
    'Determination despite losses'
  ],
  
  eventPool: ['threat_high', 'morale_crisis', 'supply_critical', 'campaign_catastrophe'],
  
  failureState: {
    title: 'Position Overrun',
    description: 'If defensive mission fails critically, the base is lost and unit is scattered.',
    narrative: 'We couldn\'t hold. The position falls. Command orders retreat and regroup.'
  },
  
  successState: {
    title: 'Against All Odds',
    description: 'Defense succeeds. Enemy offensive stalls. Unit proves its mettle under fire.',
    narrative: 'We held. Against everything they threw at us, we held. Command recognizes our sacrifice.'
  }
};

/**
 * CHAPTER 4: VICTORY
 * Days 111-150 (Approximate)
 * Theme: Final push, coordination, decisive action
 * Players execute final campaign objective
 * Story Goal: Reach "Final Operation" mission - complete the campaign
 */
export const CHAPTER_4 = {
  id: 'chapter_4',
  title: 'Victory',
  subtitle: 'The Final Push',
  description: 'Having survived the crucible, your unit is now the tip of the spear. Command authorizes the final operation. This is what all the training, all the losses, all the sacrifice was for. Time to finish the mission.',
  
  theme: {
    tone: 'determined_professional',
    color: 'greens_and_golds',
    pacing: 'climactic'
  },
  
  openingNarrative: 'The enemy is weakened. Your unit survived when others fell. Now command plans the final offensive. Every asset. Every allied unit. Coordinated strikes across multiple objectives. This is combined arms warfare at its peak. This is what victory looks like. Not a single moment of glory, but the culmination of disciplined execution, tactical excellence, and unwavering resolve.',
  
  storyBeats: [
    {
      id: 'ch4_beat_1',
      day: 111,
      title: 'Strategic Planning',
      description: 'Command briefs final operation. Multiple phases. Coordinated timing. Your unit has a critical role.',
      narrative: 'This is the plan. Months of intelligence. Weeks of preparation. Everything converges now.',
      eventHooks: ['strategic_planning', 'command_briefing'],
      characterSpeech: {
        command: 'Your unit will spearhead the assault. We need precision. We need excellence.',
        protagonist: 'You\'ll get both. My soldiers are ready.'
      }
    },
    {
      id: 'ch4_beat_2',
      day: 123,
      title: 'Allied Assembly',
      description: 'Forces from multiple theaters converge. This is the largest coordinated operation of the campaign.',
      narrative: 'We\'re not alone anymore. Units from across the theater. Air support. Artillery. Armor. This is overwhelming force.',
      eventHooks: ['allied_reinforcement', 'supply_surge'],
      characterSpeech: {
        allied_commander: 'Your unit\'s reputation is well earned. Let\'s finish this together.',
        protagonist: 'Agreed. Coordinated. Professional. Decisive.'
      }
    },
    {
      id: 'ch4_beat_3',
      day: 135,
      title: 'Final Preparation',
      description: 'Equipment checks. Briefings. Every soldier knows their role. This is what training was for.',
      narrative: 'We\'ve done this before. But never at this scale. Never with so much at stake.',
      eventHooks: ['equipment_optimal', 'morale_peak'],
      characterSpeech: {
        protagonist: 'Everyone listen. We execute by the numbers. Trust your training. Trust each other. We finish this.'
      }
    },
    {
      id: 'ch4_beat_4',
      day: 147,
      title: 'Final Operation',
      description: 'The operation launches. Every unit. Every asset. Synchronized assault on enemy positions.',
      narrative: 'This is it. Everything we trained for. Everything we fought for. This is how campaigns end. With overwhelming coordinated force.',
      eventHooks: [],
      missionTrigger: 'final_campaign_operation',
      characterSpeech: {
        protagonist: 'All units, this is command. Operation is go. Execute with precision. Bring everyone home.'
      }
    }
  ],
  
  thematicElements: [
    'Professional excellence',
    'Coordinated warfare',
    'Culmination of training and experience',
    'Decisive action',
    'Completing the mission'
  ],
  
  eventPool: ['morale_triumph', 'supply_optimal', 'campaign_victory'],
  
  failureState: {
    title: 'Operational Failure',
    description: 'If final operation fails, campaign objectives are not met. Strategic goals remain incomplete.',
    narrative: 'We gave everything. But it wasn\'t enough. The campaign ends without decisive victory.'
  },
  
  successState: {
    title: 'Mission Accomplished',
    description: 'Final operation succeeds. Enemy positions neutralized. Campaign objectives achieved.',
    narrative: 'Mission complete. Objectives achieved. Your unit executed with excellence. This is what victory looks like.'
  }
};

/**
 * STORY THREADS
 * Ongoing narrative elements that can be triggered throughout the chapters
 */
export const STORY_THREADS = {
  unit_cohesion: {
    id: 'unit_cohesion',
    title: 'Brothers in Arms',
    description: 'Building and maintaining unit cohesion through combat',
    chapters: ['chapter_1', 'chapter_2', 'chapter_3', 'chapter_4'],
    keyEvents: ['unit_bonding', 'veteran_training', 'morale_boost']
  },
  
  tactical_evolution: {
    id: 'tactical_evolution',
    title: 'Adapting to Win',
    description: 'Learning and adapting tactics based on battlefield experience',
    chapters: ['chapter_1', 'chapter_2', 'chapter_3', 'chapter_4'],
    keyEvents: ['tactical_insight', 'enemy_analysis', 'doctrine_update']
  },
  
  command_relationship: {
    id: 'command_relationship',
    title: 'Chain of Command',
    description: 'Relationship with higher command and strategic decision-making',
    chapters: ['chapter_1', 'chapter_2', 'chapter_3', 'chapter_4'],
    keyEvents: ['command_recognition', 'strategic_input', 'operational_authority']
  },
  
  allied_cooperation: {
    id: 'allied_cooperation',
    title: 'Combined Arms',
    description: 'Building relationships and coordination with allied forces',
    chapters: ['chapter_2', 'chapter_3', 'chapter_4'],
    keyEvents: ['allied_communication', 'joint_operation', 'allied_reinforcement']
  },
  
  supply_logistics: {
    id: 'supply_logistics',
    title: 'Beans and Bullets',
    description: 'Managing supply lines and logistics under combat conditions',
    chapters: ['chapter_1', 'chapter_2', 'chapter_3'],
    keyEvents: ['supply_delivery', 'supply_interdiction', 'equipment_upgrade']
  },
  
  leadership_test: {
    id: 'leadership_test',
    title: 'Command Under Fire',
    description: 'Testing and proving leadership capabilities in crisis',
    chapters: ['chapter_2', 'chapter_3', 'chapter_4'],
    keyEvents: ['crisis_decision', 'morale_crisis', 'command_recognition']
  }
};

/**
 * KEY MISSIONS - Story trigger points
 */
export const KEY_MISSIONS = [
  { id: 'first_strike_operation', chapter: 'chapter_1', day: '~21' },
  { id: 'coordinated_offensive', chapter: 'chapter_2', day: '~66' },
  { id: 'desperate_defense', chapter: 'chapter_3', day: '~95' },
  { id: 'final_campaign_operation', chapter: 'chapter_4', day: '~147' }
];

const vanillaStoryLibrary = {
  CHAPTER_1,
  CHAPTER_2,
  CHAPTER_3,
  CHAPTER_4,
  STORY_THREADS,
  KEY_MISSIONS
};

export default vanillaStoryLibrary;